import { useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, MapPin, Phone } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePublicMenu } from "@/hooks/usePublicMenu";
import { usePublicCart } from "@/hooks/usePublicCart";
import { usePublicOrder } from "@/hooks/usePublicOrder";
import { PublicProductCard } from "@/components/public/PublicProductCard";
import { PublicCart } from "@/components/public/PublicCart";
import { PublicCheckoutDialog } from "@/components/public/PublicCheckoutDialog";
import { ProductDetailSheet, SelectedAddon } from "@/components/public/ProductDetailSheet";
import type { PublicProduct } from "@/hooks/usePublicMenu";

export default function PublicMenuPage() {
  const { slug } = useParams<{ slug: string }>();
  const { restaurant, categories, products, isLoading, error } = usePublicMenu(slug || "", "delivery");
  const cart = usePublicCart();
  const { createOrder, isSubmitting } = usePublicOrder();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<PublicProduct | null>(null);
  const [productSheetOpen, setProductSheetOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Cardápio não disponível</h1>
          <p className="text-muted-foreground">{error || "Restaurante não encontrado"}</p>
        </div>
      </div>
    );
  }

  const filteredProducts = selectedCategory === "all"
    ? products
    : products.filter((p) => p.category_id === selectedCategory);

  const getItemQuantity = (productId: string) => {
    const item = cart.items.find((i) => i.id === productId);
    return item?.quantity || 0;
  };

  const handleProductClick = (product: PublicProduct) => {
    setSelectedProduct(product);
    setProductSheetOpen(true);
  };

  const handleAddToCart = (product: PublicProduct, quantity: number, notes: string, addons: SelectedAddon[]) => {
    cart.addItem(
      {
        id: product.id,
        name: product.name,
        price: product.price,
        image_url: product.image_url,
      },
      quantity,
      notes || undefined,
      addons.length > 0 ? addons : undefined
    );
    setProductSheetOpen(false);
    setSelectedProduct(null);
  };

  const handleCheckout = async (data: {
    orderType: "delivery" | "pickup";
    customerName: string;
    customerPhone: string;
    deliveryAddress?: string;
    notes?: string;
  }) => {
    const success = await createOrder({
      restaurantId: restaurant.id,
      orderType: data.orderType,
      customerName: data.customerName,
      customerPhone: data.customerPhone,
      deliveryAddress: data.deliveryAddress,
      deliveryFee: data.orderType === "delivery" ? restaurant.delivery_fee : 0,
      notes: data.notes,
      items: cart.items,
    });

    if (success) {
      cart.clearCart();
    }
    return success;
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="bg-primary text-primary-foreground p-6">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          {restaurant.logo_url && (
            <img
              src={restaurant.logo_url}
              alt={restaurant.name}
              className="w-16 h-16 rounded-full bg-white object-cover"
            />
          )}
          <div>
            <h1 className="text-2xl font-bold">{restaurant.name}</h1>
            {restaurant.address && (
              <p className="text-sm opacity-90 flex items-center gap-1 mt-1">
                <MapPin className="h-3 w-3" />
                {restaurant.address}
              </p>
            )}
            {restaurant.phone && (
              <p className="text-sm opacity-90 flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {restaurant.phone}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="sticky top-0 bg-background border-b z-10">
        <div className="max-w-2xl mx-auto">
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
            <TabsList className="w-full justify-start overflow-x-auto h-auto p-1">
              <TabsTrigger value="all" className="text-xs">Todos</TabsTrigger>
              {categories.map((cat) => (
                <TabsTrigger key={cat.id} value={cat.id} className="text-xs whitespace-nowrap">
                  {cat.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Products */}
      <div className="max-w-2xl mx-auto p-4 space-y-3">
        {filteredProducts.map((product) => (
          <PublicProductCard
            key={product.id}
            product={product}
            quantity={getItemQuantity(product.id)}
            onAdd={() => handleProductClick(product)}
            onRemove={() => cart.updateQuantityById(product.id, getItemQuantity(product.id) - 1)}
          />
        ))}
      </div>

      {/* Cart Button */}
      <PublicCart
        items={cart.items}
        subtotal={cart.subtotal}
        itemCount={cart.itemCount}
        deliveryFee={restaurant.delivery_enabled ? restaurant.delivery_fee : 0}
        onUpdateQuantity={cart.updateQuantity}
        onRemove={cart.removeItem}
        onCheckout={() => setCheckoutOpen(true)}
        disabled={isSubmitting}
      />

      {/* Checkout Dialog */}
      <PublicCheckoutDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        restaurant={restaurant}
        items={cart.items}
        subtotal={cart.subtotal}
        onSubmit={handleCheckout}
        isSubmitting={isSubmitting}
      />

      {/* Product Detail Sheet */}
      <ProductDetailSheet
        open={productSheetOpen}
        onOpenChange={setProductSheetOpen}
        product={selectedProduct}
        onAddToCart={handleAddToCart}
      />
    </div>
  );
}
